import React, { useEffect, useState, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ShoppingBag, Menu, X, User, Search } from "lucide-react";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import { api, fmtPrice } from "../lib/api";
import NotificationsBell from "./NotificationsBell";

const collections = ["Recovery", "Metabolic", "Growth", "Neuro", "Skin & Longevity", "Immune", "Laboratory Supplies"];
const links = [
  { to: "/products", label: "Products" },
  { to: "/#process", label: "COAs" },
];
// Full link set used by mobile drawer (keeps parity with footer sections)
const mobileLinks = [
  { to: "/products", label: "Products" },
  { to: "/#process", label: "COAs" },
  { to: "/#journal", label: "Journal" },
  { to: "/#why", label: "About" },
  { to: "/#contact", label: "Support" },
  { to: "/faq", label: "FAQ" },
];

export default function Navbar() {
  const { count, setOpen } = useCart();
  const { user } = useAuth();
  const [scrolled, setScrolled] = useState(false);
  const [mobile, setMobile] = useState(false);
  const [mega, setMega] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const location = useLocation();
  const navigate = useNavigate();
  const authed = user && user.user_id;
  const inputRef = useRef(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setMobile(false); setSearchOpen(false); setMega(false); }, [location]);

  useEffect(() => {
    if (!searchOpen) return;
    const h = setTimeout(() => {
      if (q.trim().length < 2) { setResults([]); return; }
      api.get("/products", { params: { q: q.trim() } }).then(({ data }) => setResults(data.slice(0, 6))).catch(() => setResults([]));
    }, 250);
    return () => clearTimeout(h);
  }, [q, searchOpen]);

  useEffect(() => { if (searchOpen && inputRef.current) inputRef.current.focus(); }, [searchOpen]);

  const submitSearch = (e) => {
    e.preventDefault();
    if (q.trim()) { navigate(`/products?q=${encodeURIComponent(q.trim())}`); setSearchOpen(false); }
  };

  const solid = scrolled || mega || searchOpen;

  return (
    <motion.header
      data-testid="main-navbar"
      initial={{ y: -80 }} animate={{ y: 0 }} transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
      style={{ top: "var(--announcement-h, 0px)" }}
      className={`fixed left-0 right-0 z-50 transition-[background-color,border-color] duration-500 ${solid ? "bg-[#0F0F10]/90 backdrop-blur-xl border-b border-white/10" : "bg-transparent border-b border-transparent"}`}
      onMouseLeave={() => setMega(false)}
    >
      <nav className="mx-auto max-w-[1500px] px-6 sm:px-10 h-20 flex items-center justify-between">
        <Link to="/" data-testid="logo-link" className="shrink-0">
          <span className="font-display font-semibold text-xl tracking-[-0.02em] text-[#F5F5F5]">Enda<span className="text-[#4ADE80]">G1</span></span>
        </Link>

        <div className="hidden lg:flex items-center gap-9 text-[12px] uppercase tracking-[0.18em] text-[#A8A8A8]">
          <Link to="/products" data-testid="nav-products" className="link-underline hover:text-[#F5F5F5] transition-colors">Products</Link>
          <button data-testid="nav-collections" onMouseEnter={() => setMega(true)} onClick={() => setMega((m) => !m)} className="link-underline hover:text-[#F5F5F5] transition-colors uppercase tracking-[0.18em]">Collections</button>
          {links.slice(1).map((l) => (
            <Link key={l.label} to={l.to} data-testid={`nav-${l.label.toLowerCase()}`} className="link-underline hover:text-[#F5F5F5] transition-colors">{l.label}</Link>
          ))}
        </div>

        <div className="flex items-center gap-5 sm:gap-6">
          <button data-testid="search-toggle" onClick={() => setSearchOpen((s) => !s)} aria-label="Search" className="text-[#A8A8A8] hover:text-[#4ADE80] transition-colors">
            {searchOpen ? <X className="h-[18px] w-[18px]" /> : <Search className="h-[18px] w-[18px]" strokeWidth={1.5} />}
          </button>
          <Link to={authed ? "/account" : "/login"} data-testid="account-link" aria-label="Account" className="hidden sm:block text-[#A8A8A8] hover:text-[#4ADE80] transition-colors"><User className="h-[18px] w-[18px]" strokeWidth={1.5} /></Link>
          <NotificationsBell authed={!!authed} />
          {authed && user.is_admin && (
            <Link to="/admin" data-testid="admin-link" className="hidden lg:block text-[10px] uppercase tracking-[0.18em] text-[#4ADE80] hover:text-[#6FE998] transition-colors">Admin</Link>
          )}
          <button data-testid="open-cart-btn" onClick={() => setOpen(true)} className="relative text-[#A8A8A8] hover:text-[#4ADE80] transition-colors" aria-label="Open cart">
            <ShoppingBag className="h-[18px] w-[18px]" strokeWidth={1.5} />
            {count > 0 && <span data-testid="cart-count-badge" className="absolute -top-2 -right-2 h-4 min-w-4 px-1 bg-[#4ADE80] text-[#0F0F10] text-[10px] font-medium flex items-center justify-center">{count}</span>}
          </button>
          <button className="lg:hidden text-[#F5F5F5]" data-testid="mobile-menu-toggle" onClick={() => setMobile((m) => !m)} aria-label="Toggle menu">{mobile ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}</button>
        </div>
      </nav>

      {/* Predictive search */}
      <AnimatePresence>
        {searchOpen && (
          <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}
            className="border-t border-white/10 bg-[#0F0F10]/95 backdrop-blur-xl" data-testid="search-panel">
            <div className="mx-auto max-w-[1500px] px-6 sm:px-10 py-6">
              <form onSubmit={submitSearch}>
                <input ref={inputRef} data-testid="predictive-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search compounds…"
                  className="w-full bg-transparent border-b border-white/20 py-3 text-2xl font-display text-[#F5F5F5] outline-none focus:border-[#4ADE80] transition-colors placeholder:text-[#7a7a7a]" />
              </form>
              {results.length > 0 && (
                <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="search-results">
                  {results.map((p) => (
                    <Link key={p.slug} to={`/products/${p.slug}`} data-testid={`search-result-${p.slug}`} className="flex items-center gap-3 p-2 hover:bg-white/[0.04] transition-colors">
                      <img src={p.image} alt={p.name} className="h-12 w-12 object-cover bg-[#1F1F1F] border border-white/10" />
                      <div className="min-w-0"><p className="text-[14px] text-[#F5F5F5] truncate">{p.name}</p><p className="text-[12px] text-[#A8A8A8]">{fmtPrice(p.price)}</p></div>
                    </Link>
                  ))}
                </div>
              )}
              {q.trim().length >= 2 && results.length === 0 && <p className="mt-5 text-[13px] text-[#7a7a7a]">No compounds found.</p>}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Mega menu */}
      <AnimatePresence>
        {mega && (
          <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}
            className="hidden lg:block border-t border-white/10 bg-[#0F0F10]/95 backdrop-blur-xl" data-testid="mega-menu" onMouseEnter={() => setMega(true)}>
            <div className="mx-auto max-w-[1500px] px-10 py-10 grid grid-cols-3 gap-10">
              <div className="col-span-2 grid grid-cols-2 gap-x-10 gap-y-4">
                {collections.map((c) => (
                  <Link key={c} to={`/products?category=${encodeURIComponent(c)}`} data-testid={`mega-${c.toLowerCase().replace(/\s|&/g, "-")}`} className="font-serif-editorial text-2xl text-[#F5F5F5] hover:text-[#4ADE80] transition-colors">{c}</Link>
                ))}
              </div>
              <div className="border-l border-white/10 pl-10">
                <p className="text-[11px] uppercase tracking-[0.2em] text-[#4ADE80] mb-4">Not sure where to start?</p>
                <p className="text-[15px] text-[#A8A8A8] font-light leading-relaxed">Take the research finder quiz to match compounds to your protocol.</p>
                <Link to="/quiz" data-testid="mega-quiz-link" className="btn-press mt-5 inline-block border border-white/20 text-[#F5F5F5] uppercase tracking-[0.16em] text-[11px] px-6 py-3 hover:border-[#4ADE80] hover:text-[#4ADE80]">Find My Compound</Link>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {mobile && (
        <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} className="lg:hidden bg-[#0F0F10] border-t border-white/10 px-6 py-8 flex flex-col gap-5 text-sm uppercase tracking-[0.18em] text-[#A8A8A8]">
          {mobileLinks.map((l) => <Link key={l.label} to={l.to} data-testid={`mobile-nav-${l.label.toLowerCase()}`} className="hover:text-[#4ADE80] transition-colors">{l.label}</Link>)}
          <Link to="/quiz" className="hover:text-[#4ADE80] transition-colors">Find My Compound</Link>
          <Link to={authed ? "/account" : "/login"} data-testid="mobile-nav-account" className="hover:text-[#4ADE80] transition-colors">{authed ? "Account" : "Sign In"}</Link>
          {authed && user.is_admin && (
            <Link to="/admin" data-testid="mobile-nav-admin" className="text-[#4ADE80] hover:text-[#6FE998] transition-colors">Admin</Link>
          )}
        </motion.div>
      )}
    </motion.header>
  );
}
